/**
 * Sipfull — PDP hero-image lightbox.
 *
 * Tap the product hero image → opens a full-screen viewer with:
 *   - Full-res image (uses the hero's current src, so brand-swapped
 *     and variant-swapped URLs are honoured)
 *   - Swipe left/right (touch) OR ‹ / › buttons (desktop) OR ← / →
 *     keys to navigate through every non-video gallery image
 *   - Native pinch-to-zoom (touch-action: pinch-zoom on the img
 *     lets the browser handle zoom + pan without a library)
 *   - Close via × button, backdrop tap, or ESC key
 *   - Counter "3 / 7" so the customer knows where they are
 *
 * Fixes Ticket #98 (2026-08-18 CRO handover) — session recordings
 * show real customers tapping the hero expecting it to expand, and
 * nothing happens. FB App webview (~69% of traffic) makes the small
 * default hero size particularly painful.
 *
 * Video thumbs are deliberately excluded from the lightbox stream.
 * Videos have their own player in the hero — swiping past a video
 * slot would be jarring, and we can't (easily) pinch-zoom a video.
 *
 * Zero dependencies. ~9KB minified. Works in FB App / Instagram
 * webviews (verified against known-broken patterns: no requestAnim-
 * Frame chaining, no `dialog` element, no CSS `backdrop-filter`
 * dependency, no ES modules, no touch-action: none on scrollable
 * containers).
 *
 * Loaded from both product sections (standard + personalised) with:
 *   {{ 'sipfull-pdp-lightbox.js' | asset_url | script_tag }}
 *
 * Preserves burnt-in image overlays (the "Various Colours Available"
 * badge, "Handmade in UK" stamp, Sipfull of Design logo card) —
 * those are part of the image asset itself, so they come through
 * unchanged. NOT preserved: the mug-color-overlay colour tint,
 * which is a separate absolutely-positioned element on the hero.
 * Lightbox shows the raw hero <img> only, so the customer sees the
 * un-tinted version. For the current PDP flow this is fine — the
 * tinted view is available on the small hero, the lightbox shows
 * the base product photo at full detail.
 */
(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────────────
  var slides       = [];     // [{ src, srcset, alt }, ...]
  var currentIdx   = 0;
  var overlay      = null;   // lazy-mounted on first open
  var imgEl        = null;
  var captionEl    = null;
  var prevBtn      = null;
  var nextBtn      = null;
  var closeBtn     = null;
  var touchStartX  = null;
  var touchStartY  = null;
  var touchStartAt = 0;

  // ─── Styles ────────────────────────────────────────────────────────
  // Injected once on first mount to keep the total asset count down.
  // All rules namespaced `.sp-lb` so nothing bleeds into the PDP.
  var STYLES = [
    '.sp-lb {',
    '  position: fixed; inset: 0; z-index: 99999;',
    '  background: rgba(0, 0, 0, 0.96);',
    '  display: none;',
    '  align-items: center; justify-content: center;',
    '  padding: 0;',
    /* Fade in on open — cheap and consistent across browsers.
       transform is intentionally omitted (transform + touch on
       Android can trigger a repaint bug in some builds). */
    '  opacity: 0; transition: opacity 0.18s ease;',
    '  overscroll-behavior: contain;',
    '  -webkit-tap-highlight-color: transparent;',
    '  user-select: none; -webkit-user-select: none;',
    '}',
    '.sp-lb.is-open {',
    '  display: flex; opacity: 1;',
    '}',
    '.sp-lb__img-wrap {',
    '  position: relative; max-width: 100%; max-height: 100%;',
    '  display: flex; align-items: center; justify-content: center;',
    '  overflow: hidden;',
    '}',
    '.sp-lb__img {',
    '  display: block;',
    '  max-width: 100vw;',
    '  max-height: calc(100vh - 40px);',
    '  width: auto; height: auto;',
    '  object-fit: contain;',
    /* touch-action: pinch-zoom lets the browser handle pinch + pan
       natively while we still get touchstart/touchmove/touchend for
       swipe detection. Falls back to swipe on browsers that ignore
       it (older Android; user can still double-tap zoom). */
    '  touch-action: pinch-zoom;',
    /* Prevent iOS long-press "save image" menu — reduces accidental
       hits during a swipe. Doesn\'t affect legit right-click on desktop. */
    '  -webkit-touch-callout: none;',
    '  user-select: none; -webkit-user-select: none;',
    '  pointer-events: auto;',
    '}',
    '.sp-lb__btn {',
    '  position: absolute; z-index: 2;',
    '  background: rgba(255, 255, 255, 0.14);',
    '  color: #fff; border: 0;',
    '  width: 48px; height: 48px;',
    '  border-radius: 50%;',
    '  font-size: 22px; line-height: 1;',
    '  cursor: pointer;',
    '  display: inline-flex; align-items: center; justify-content: center;',
    '  transition: background 0.15s ease;',
    '  padding: 0;',
    '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;',
    '}',
    '.sp-lb__btn:hover { background: rgba(255, 255, 255, 0.26); }',
    '.sp-lb__btn:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }',
    '.sp-lb__btn:disabled { opacity: 0.25; cursor: default; }',
    '.sp-lb__btn--prev  { left: 20px; top: 50%; transform: translateY(-50%); }',
    '.sp-lb__btn--next  { right: 20px; top: 50%; transform: translateY(-50%); }',
    '.sp-lb__btn--close { top: 16px; right: 16px; width: 40px; height: 40px; font-size: 20px; }',
    '.sp-lb__caption {',
    '  position: absolute; bottom: 16px; left: 50%;',
    '  transform: translateX(-50%);',
    '  color: #fff; font-size: 13px; letter-spacing: 0.06em;',
    '  font-weight: 500;',
    '  background: rgba(0, 0, 0, 0.5);',
    '  padding: 5px 12px; border-radius: 4px;',
    '  pointer-events: none; z-index: 2;',
    '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;',
    '}',
    '.sp-lb__caption--hidden { display: none; }',
    /* Signal to the customer that the hero is tappable. Not a
       full-blown zoom icon (feels heavy) — just a subtle cursor
       hint on desktop and a soft focus ring on keyboard nav. */
    '[data-sp-hero] { cursor: zoom-in; }',
    '@media (max-width: 600px) {',
    '  .sp-lb__btn--prev { left: 6px; width: 40px; height: 40px; font-size: 18px; }',
    '  .sp-lb__btn--next { right: 6px; width: 40px; height: 40px; font-size: 18px; }',
    '  .sp-lb__caption { bottom: 10px; font-size: 11px; padding: 4px 10px; }',
    '}',
  ].join('\n');

  function injectStyles() {
    if (document.getElementById('sp-lb-styles')) return;
    var s = document.createElement('style');
    s.id = 'sp-lb-styles';
    s.textContent = STYLES;
    document.head.appendChild(s);
  }

  // ─── DOM mount ─────────────────────────────────────────────────────
  function mountOverlay() {
    if (overlay) return;
    injectStyles();
    overlay = document.createElement('div');
    overlay.className = 'sp-lb';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Product image viewer');
    overlay.innerHTML = [
      '<button type="button" class="sp-lb__btn sp-lb__btn--prev" aria-label="Previous image">‹</button>',
      '<div class="sp-lb__img-wrap">',
      '  <img class="sp-lb__img" alt="" draggable="false">',
      '</div>',
      '<button type="button" class="sp-lb__btn sp-lb__btn--next" aria-label="Next image">›</button>',
      '<button type="button" class="sp-lb__btn sp-lb__btn--close" aria-label="Close viewer">×</button>',
      '<div class="sp-lb__caption">1 / 1</div>',
    ].join('');
    document.body.appendChild(overlay);
    imgEl     = overlay.querySelector('.sp-lb__img');
    captionEl = overlay.querySelector('.sp-lb__caption');
    prevBtn   = overlay.querySelector('.sp-lb__btn--prev');
    nextBtn   = overlay.querySelector('.sp-lb__btn--next');
    closeBtn  = overlay.querySelector('.sp-lb__btn--close');

    prevBtn .addEventListener('click', function (e) { e.stopPropagation(); show(currentIdx - 1); });
    nextBtn .addEventListener('click', function (e) { e.stopPropagation(); show(currentIdx + 1); });
    closeBtn.addEventListener('click', function (e) { e.stopPropagation(); close(); });
    /* Prevent image clicks from closing (backdrop-close needs to fire
       ONLY when the customer taps the dark border around the image). */
    imgEl.addEventListener('click',   function (e) { e.stopPropagation(); });
    overlay.addEventListener('click', close);

    /* Touch swipe — capture start, react on end. Skip if the customer
       is mid-pinch (two fingers) so we don\'t swipe them off a zoomed
       image. Also skip if the touch was too short (<50ms) or the
       horizontal delta was small (<40px) — those look like taps. */
    overlay.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { touchStartX = null; return; }
      touchStartX  = e.touches[0].clientX;
      touchStartY  = e.touches[0].clientY;
      touchStartAt = Date.now();
    }, { passive: true });
    overlay.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      if (e.changedTouches.length !== 1) return;
      var dx = e.changedTouches[0].clientX - touchStartX;
      var dy = e.changedTouches[0].clientY - touchStartY;
      var dt = Date.now() - touchStartAt;
      touchStartX = touchStartY = null;
      /* Reject taps (small movement) and slow drags (probably a
         pan on a zoomed image, not a swipe). */
      if (Math.abs(dx) < 40 || dt > 800) return;
      /* Reject clearly-vertical gestures — probably a pull-to-refresh
         or a scroll gesture the browser didn\'t catch first. */
      if (Math.abs(dy) > Math.abs(dx)) return;
      show(currentIdx + (dx < 0 ? 1 : -1));
    }, { passive: true });

    document.addEventListener('keydown', onKey);
  }

  function onKey(e) {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape')     close();
    else if (e.key === 'ArrowLeft')  show(currentIdx - 1);
    else if (e.key === 'ArrowRight') show(currentIdx + 1);
  }

  // ─── Slide navigation ──────────────────────────────────────────────
  function show(idx) {
    if (!slides.length) return;
    /* Clamp to bounds — no wrap-around. Disabled arrows at the ends
       give the customer a clear "you\'ve reached the end" signal. */
    currentIdx = Math.max(0, Math.min(idx, slides.length - 1));
    var s = slides[currentIdx];
    imgEl.src = s.src;
    if (s.srcset) imgEl.setAttribute('srcset', s.srcset);
    else          imgEl.removeAttribute('srcset');
    imgEl.alt = s.alt || 'Product image';
    if (slides.length > 1) {
      captionEl.classList.remove('sp-lb__caption--hidden');
      captionEl.textContent = (currentIdx + 1) + ' / ' + slides.length;
    } else {
      captionEl.classList.add('sp-lb__caption--hidden');
    }
    prevBtn.disabled = currentIdx === 0;
    nextBtn.disabled = currentIdx === slides.length - 1;
  }

  function open(startIdx) {
    mountOverlay();
    if (!slides.length) return;
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    show(startIdx || 0);
  }

  function close() {
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    /* Blank the src so a large image doesn\'t sit in memory while
       the modal is closed — helps FB App on older Android devices. */
    if (imgEl) imgEl.src = '';
  }

  // ─── Slide collection ──────────────────────────────────────────────
  // Rebuilt on every open so brand-swapped + variant-swapped URLs are
  // honoured. Reads data-hero-src / data-hero-srcset off the thumbs
  // — those are the same URLs the thumb-click handler swaps into the
  // hero. Video thumbs are excluded (no swipe-past-video).
  function collectSlides(root) {
    var out = [];
    var seen = {};
    var thumbs = root.querySelectorAll('[data-sp-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      var t = thumbs[i];
      if ((t.getAttribute('data-media-type') || '') === 'video') continue;
      /* Skip thumbs whose enclosing element is display:none — brand-
         swap hides video slots, and older visits may leave some
         variant thumbs hidden depending on availability. */
      if (t.offsetParent === null && t.style.display === 'none') continue;
      var src = t.getAttribute('data-hero-src');
      if (!src) continue;
      if (seen[src]) continue;
      seen[src] = 1;
      var img = t.querySelector('img');
      out.push({
        src:    src,
        srcset: t.getAttribute('data-hero-srcset') || '',
        alt:    (img && img.alt) || 'Product image',
      });
    }
    return out;
  }

  // ─── Wire the hero image ───────────────────────────────────────────
  function wire() {
    /* Delegated listener at document level so the lightbox works
       whether the hero is in the standard product section or the
       personalised one, and survives any DOM rewiring the section
       does. Only fires on the hero <img>, not on its overlay
       controls (they .stopPropagation on their own handlers). */
    document.addEventListener('click', function (e) {
      var hero = e.target && e.target.closest && e.target.closest('[data-sp-hero]');
      if (!hero) return;
      /* Skip if the hero is a <video> or the click landed on a nav
         arrow button that sits on top of the hero (they have their
         own handlers). */
      if (hero.tagName === 'VIDEO') return;
      if (e.target.closest('[data-sp-hero-prev], [data-sp-hero-next]')) return;
      /* Skip when a modifier is held — customer probably wants the
         native "open image in new tab" browser action. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      var root = hero.closest('[data-sipfull-product]') || document;
      slides = collectSlides(root);
      if (!slides.length) return;
      /* Pick the slide that matches the CURRENT hero src so the
         lightbox opens on the same image the customer is viewing.
         Fall back to slide 0 if no match (defensive — should always
         match). */
      var currentSrc = hero.getAttribute('src') || '';
      var startIdx = 0;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].src === currentSrc) { startIdx = i; break; }
      }
      open(startIdx);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();
